import * as React from 'react';
import styled, { css } from 'styled-components';
import { OptionType, IDropdownOption } from '../dropdown-props';
import { IItemRenderProps } from '../../listview';
import { OptionGroup, StyledLabel, OptionDivider, Option, OptionState } from './dropdown-options';
import { Checkbox } from '../../checkbox';

interface IDropdownItemRendererProps {
    optionType?: OptionType;
    multiSelect: boolean;
    showSelectAll: boolean;
    itemProps: IItemRenderProps;
    isGrouped: boolean;
    optionState: OptionState;
    isOptionSelected: boolean;
    itemHeight: number;
    onSelectAllChange(): void;
    onCloseWithoutChange(): void;
}

interface ICommonStyleProps {
    $isGrouped?: boolean;
}

const commonStyle = css<ICommonStyleProps>`
        padding-left: ${({ $isGrouped, theme: { semantics: { gap } } }) => ($isGrouped ? `${gap.xxl}` : `${gap.xxl}`)};
        width: 100%;
        height: 100%;
        align-items: center;
        display: flex;
        box-sizing: border-box;
    `;

    const StyledCheckbox = styled(Checkbox)`
        ${commonStyle};
        .ap-checkbox {
            flex-grow: 0;
            flex-shrink: 0;
        }
        .ap-checkbox-label {
            padding-left: ${({ theme: { semantics: { gap } } }) => `${gap.xl}`};
            color: ${({ theme: { dropdown: { colors } } }) => colors.default.optionTextColor};
            width: calc(100% - 70px);
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
        }
    `;

    interface IOptionTitleProps extends React.HTMLProps<HTMLSpanElement>, ICommonStyleProps { }

    export const OptionTitle = styled.span<IOptionTitleProps>`
        ${commonStyle}
        width: calc(100% - 30px);
    `;

export class DropdownItem extends React.PureComponent<IDropdownItemRendererProps> {
    render() {
        return this.renderItem();
    }

    private renderItem() {
        const { multiSelect, itemProps, isGrouped, optionState, itemHeight } = this.props;
        const { data, data: { text, type } } = itemProps;

        switch (type) {
            case OptionType.OptionGroup:
                return (
                    <>
                        <OptionDivider $topPosition={itemHeight} />
                        <OptionGroup title={text}>
                            <StyledLabel>{text}</StyledLabel>
                        </OptionGroup>
                    </>
                );

            case OptionType.SelectAll:
                return this.renderSelectAll(data);

            default: break;
        }

        if (multiSelect) {
            return this.renderMultiSelectItems(itemProps);
        }

        return (
            <Option
                highlightItemOnSelect={!multiSelect} title={text}
                optionState={optionState} onClick={this.onClick}>
                <OptionTitle $isGrouped={isGrouped}>
                    <StyledLabel>{text}</StyledLabel>
                </OptionTitle>
            </Option>
        );
    }

    private renderMultiSelectItems(itemProps: IItemRenderProps) {
        const { data: { text } } = itemProps;
        const { multiSelect, isOptionSelected, optionState } = this.props;
        return (
            <Option optionState={optionState} highlightItemOnSelect={!multiSelect}>
                <StyledCheckbox $isGrouped checked={isOptionSelected} onClick={this.onClick} label={text} title={text} />
            </Option >
        );
    }

    private renderSelectAll({ text }: IDropdownOption) {
        const { showSelectAll, isOptionSelected, optionState, itemHeight, onSelectAllChange } = this.props;
        if (showSelectAll) {
            return (
                <>
                    <Option optionState={optionState} >
                        <StyledCheckbox checked={isOptionSelected} label={text} onClick={onSelectAllChange} />
                    </Option>
                    <OptionDivider $topPosition={itemHeight} />
                </>
            );
        }
        return null;
    }

    private onClick = () => {
        const { itemProps: { toggleSelection }, multiSelect, isOptionSelected, onCloseWithoutChange } = this.props;
        if (isOptionSelected && !multiSelect) {
            onCloseWithoutChange();
            return;
        }
        toggleSelection();
    }
}
